import { useMemo, useState } from "react";
import {
  Camera,
  FileText,
  Hash,
  ShieldCheck,
  ChevronLeft,
  CheckCircle2,
  Delete as BackspaceIcon,
} from "lucide-react";
import {
  Modal,
  Field,
  Select,
  MultiSelect,
  ImageUploader,
  FileUploader,
  useLookup,
} from "./VehicleFormUI";

const Footer = ({
  onCancel,
  onSave,
  saveLabel = "Save",
  disabled,
  onRemove,
}) => (
  <>
    {onRemove && (
      <button
        type="button"
        className="vc-btn vc-btn--danger-ghost"
        onClick={onRemove}
      >
        Remove
      </button>
    )}
    <span className="vc-spacer" />
    <button type="button" className="vc-btn vc-btn--ghost" onClick={onCancel}>
      Cancel
    </button>
    <button
      type="button"
      className="vc-btn vc-btn--primary"
      onClick={onSave}
      disabled={disabled}
    >
      {saveLabel}
    </button>
  </>
);

/* ================================================================== */
/* A) Vehicle photos                                                   */
/* ================================================================== */
export function PhotosModal({ value = [], onSave, onClose }) {
  const [files, setFiles] = useState(value);

  return (
    <Modal
      title="Vehicle Photos"
      subtitle="Upload clear photos of the vehicle"
      icon={Camera}
      onClose={onClose}
      width={600}
      footer={
        <Footer
          onCancel={onClose}
          saveLabel={`Save ${files.length ? `(${files.length})` : ""}`}
          onSave={() => {
            onSave(files);
            onClose();
          }}
        />
      }
    >
      <ImageUploader files={files} onChange={setFiles} max={12} />
    </Modal>
  );
}

/* ================================================================== */
/* B) License                                                          */
/* ================================================================== */
const EMPTY_LICENSE = {
  license_number: "",
  license_class_id: "",
  issue_date: "",
  expiry_date: "",
  traffic_unit: "",
  photos: [],
};

export function LicenseModal({ value, onSave, onClose }) {
  const [d, setD] = useState(value || EMPTY_LICENSE);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  const { data: classes, loading } = useLookup(
    "/api/portal/vehicle-license-classes",
    { pick: (data) => data?.license_classes?.data },
  );

  const classOptions = classes.map((c) => ({
    value: c.id,
    label: `${c.name_en} — ${c.name_ar}`,
  }));

  const dateError =
    d.issue_date && d.expiry_date && d.expiry_date < d.issue_date
      ? "Expiry date must be after issue date"
      : "";

  return (
    <Modal
      title="License"
      subtitle="Vehicle license details"
      icon={FileText}
      onClose={onClose}
      width={680}
      footer={
        <Footer
          onCancel={onClose}
          disabled={!!dateError}
          onRemove={
            value
              ? () => {
                  onSave(null);
                  onClose();
                }
              : null
          }
          onSave={() => {
            onSave(d);
            onClose();
          }}
        />
      }
    >
      <div className="vc-grid vc-grid--2">
        <Field label="License Number">
          <input
            type="number"
            className="vc-input"
            placeholder="Enter license number"
            value={d.license_number}
            onChange={(e) => set("license_number", e.target.value)}
          />
        </Field>
        <Field label="License Class">
          <Select
            value={d.license_class_id}
            options={classOptions}
            loading={loading}
            placeholder="Select class"
            onChange={(v) => set("license_class_id", v)}
          />
        </Field>
        <Field label="Issue Date">
          <input
            type="date"
            className="vc-input"
            value={d.issue_date}
            onChange={(e) => set("issue_date", e.target.value)}
          />
        </Field>
        <Field label="Expiry Date" error={dateError}>
          <input
            type="date"
            className="vc-input"
            value={d.expiry_date}
            min={d.issue_date || undefined}
            onChange={(e) => set("expiry_date", e.target.value)}
          />
        </Field>
        <Field label="Traffic Unit" className="vc-col-full">
          <input
            type="text"
            className="vc-input"
            placeholder="e.g. Nasr City Traffic Unit"
            value={d.traffic_unit}
            onChange={(e) => set("traffic_unit", e.target.value)}
          />
        </Field>
        <Field label="License Photos" className="vc-col-full">
          <ImageUploader
            files={d.photos}
            onChange={(f) => set("photos", f)}
            max={6}
            compact
          />
        </Field>
      </div>
    </Modal>
  );
}

/* ================================================================== */
/* C) Plate (multi-step)                                               */
/* ================================================================== */

export function PlatePreview({ type, left = "", right = "", small = false }) {
  const bg = type?.color_code || "#0077C8";
  const fg = type?.text_color || "#FFFFFF";
  return (
    <div
      className={`vc-plate ${small ? "is-small" : ""}`}
      style={{ borderColor: bg }}
    >
      <div className="vc-plate-head" style={{ background: bg, color: fg }}>
        <span>Egypt</span>
        <span>مصر</span>
      </div>
      <div className="vc-plate-body">
        <div className="vc-plate-cell">{left || "- - -"}</div>
        <div className="vc-plate-cell" dir="rtl">
          {right ? right.split("").join(" ") : "- - -"}
        </div>
      </div>
    </div>
  );
}

const secKey = (s) => s.position || s.code;

export function PlateModal({ value, onSave, onClose }) {
  const { data: types, loading } = useLookup("/api/plate-types", {
    pick: (d) => d?.plate_types,
  });

  const [type, setType] = useState(value?.type || null);
  const [vals, setVals] = useState({
    left: value?.plate_left || "",
    right: value?.plate_right || "",
  });
  const [photos, setPhotos] = useState(value?.photos || []);
  const [step, setStep] = useState(0);

  // numbers first, then letters (like the design)
  const sections = useMemo(
    () =>
      [...(type?.layout?.sections || [])]
        .filter((s) => s.is_active !== false)
        .sort(
          (a, b) =>
            (a.input_type === "numbers" ? 0 : 1) -
              (b.input_type === "numbers" ? 0 : 1) ||
            a.sort_order - b.sort_order,
        ),
    [type],
  );

  const reviewStep = sections.length + 1;
  const doneStep = sections.length + 2;
  const sec = step >= 1 && step <= sections.length ? sections[step - 1] : null;
  const curVal = sec ? vals[secKey(sec)] || "" : "";

  // characters allowed for the current section (comes from the API)
  const keys = useMemo(
    () =>
      [...(sec?.allowed_set?.values || [])]
        .filter((v) => v.is_active !== false)
        .sort((a, b) => a.sort_order - b.sort_order),
    [sec],
  );

  const chooseType = (t) => {
    if (type?.id !== t.id) setVals({ left: "", right: "" });
    setType(t);
  };

  const press = (ch) => {
    if (!sec) return;
    setVals((p) => {
      const cur = p[secKey(sec)] || "";
      if (cur.length >= (sec.max_length || 10)) return p;
      return { ...p, [secKey(sec)]: cur + ch };
    });
  };
  const backspace = () => {
    if (!sec) return;
    setVals((p) => ({
      ...p,
      [secKey(sec)]: (p[secKey(sec)] || "").slice(0, -1),
    }));
  };

  const sectionValid =
    !sec || !sec.is_required || curVal.length >= (sec.min_length || 1);

  const confirm = () => {
    onSave({
      plate_type_id: type.id,
      type,
      plate_left: vals.left,
      plate_right: vals.right,
      photos,
    });
    setStep(doneStep);
  };

  const titles = {
    type: "Plate Type",
    numbers: "Enter Plate Numbers",
    letters: "Enter Plate Letters",
    review: "Review & Photos",
    done: "Plate Added",
  };
  const title =
    step === 0
      ? titles.type
      : step === doneStep
        ? titles.done
        : step === reviewStep
          ? titles.review
          : sec?.input_type === "numbers"
            ? titles.numbers
            : titles.letters;

  let footer = null;
  if (step === 0)
    footer = (
      <Footer
        onCancel={onClose}
        saveLabel="Continue"
        disabled={!type || sections.length === 0}
        onSave={() => setStep(1)}
        onRemove={
          value
            ? () => {
                onSave(null);
                onClose();
              }
            : null
        }
      />
    );
  else if (sec)
    footer = (
      <Footer
        onCancel={() => setStep(step - 1)}
        saveLabel="Next"
        disabled={!sectionValid}
        onSave={() => setStep(step + 1)}
      />
    );
  else if (step === reviewStep)
    footer = (
      <Footer
        onCancel={() => setStep(step - 1)}
        saveLabel="Confirm"
        onSave={confirm}
      />
    );
  else
    footer = (
      <button
        type="button"
        className="vc-btn vc-btn--primary vc-btn--block"
        onClick={onClose}
      >
        Back to vehicle data
      </button>
    );

  return (
    <Modal
      title={title}
      subtitle={
        step > 0 && step < doneStep && type
          ? `${type.name_en} — ${type.name_ar}`
          : "Choose the plate type of this vehicle"
      }
      icon={Hash}
      onClose={onClose}
      width={520}
      footer={footer}
      headerLeft={
        step > 0 && step < doneStep ? (
          <button
            type="button"
            className="vc-icon-btn"
            onClick={() => setStep(step - 1)}
          >
            <ChevronLeft size={18} />
          </button>
        ) : null
      }
    >
      {/* ---------- Step 0: type ---------- */}
      {step === 0 && (
        <div className="vc-plate-types">
          {loading && <p className="vc-muted">Loading plate types...</p>}
          {types
            .filter((t) => t.is_active !== false)
            .map((t) => (
              <button
                type="button"
                key={t.id}
                className={`vc-plate-type ${type?.id === t.id ? "is-selected" : ""}`}
                onClick={() => chooseType(t)}
              >
                <PlatePreview type={t} small left="" right="" />
                <div className="vc-plate-type-info">
                  <strong>
                    {t.name_en} <em>{t.name_ar}</em>
                  </strong>
                  <small>{t.description}</small>
                </div>
                <span className="vc-radio" />
              </button>
            ))}
        </div>
      )}

      {/* ---------- Section steps (numbers / letters) ---------- */}
      {sec && (
        <div className="vc-plate-step">
          <PlatePreview type={type} left={vals.left} right={vals.right} />
          <p className="vc-plate-count">
            {sec.input_type === "numbers" ? "Numbers" : "Letters"} (
            {curVal.length}/{sec.max_length})
          </p>

          <div
            className="vc-plate-slots"
            dir={sec.input_type === "letters" ? "rtl" : "ltr"}
          >
            {Array.from({ length: sec.max_length }).map((_, i) => (
              <span
                key={i}
                className={`vc-plate-slot ${i === curVal.length ? "is-active" : ""}`}
              >
                {curVal[i] || ""}
              </span>
            ))}
          </div>

               {keys.length === 0 ? (
            <p className="vc-muted">
              No allowed characters are configured for this section.
            </p>
          ) : sec.input_type === "numbers" ? (
            <div className="vc-keypad">
              {keys.map((k) => (
                <button type="button" key={k.id} onClick={() => press(k.value)}>
                  {k.display_value || k.value}
                </button>
              ))}
              <button type="button" className="is-wide" onClick={backspace}>
                <BackspaceIcon size={18} />
              </button>
            </div>
          ) : (
            <div className="vc-letters" dir="rtl">
              {keys.map((k) => (
                <button type="button" key={k.id} onClick={() => press(k.value)}>
                  {k.display_value || k.value}
                </button>
              ))}
              <button type="button" className="is-wide" onClick={backspace}>
                <BackspaceIcon size={18} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---------- Review + photos ---------- */}
      {step === reviewStep && (
        <div className="vc-plate-step">
          <PlatePreview type={type} left={vals.left} right={vals.right} />
          <Field label="Plate Photos" className="vc-col-full vc-mt">
            <ImageUploader
              files={photos}
              onChange={setPhotos}
              max={4}
              compact
            />
          </Field>
        </div>
      )}

      {/* ---------- Done ---------- */}
      {step === doneStep && (
        <div className="vc-plate-step vc-plate-done">
          <span className="vc-done-icon">
            <CheckCircle2 size={44} />
          </span>
          <h4>Plate added successfully</h4>
          <p>The plate will be saved with the vehicle.</p>
          <PlatePreview type={type} left={vals.left} right={vals.right} />
        </div>
      )}
    </Modal>
  );
}

/* ================================================================== */
/* D) Insurance                                                        */
/* ================================================================== */
const EMPTY_INSURANCE = {
  insurance_company_id: "",
  policy_type_id: "",
    coverage_type_ids: [],
  policy_number: "",
  start_date: "",
  end_date: "",
  document: null,
  images: [],
};

export function InsuranceModal({ value, onSave, onClose }) {
  const [d, setD] = useState(value || EMPTY_INSURANCE);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  const companies = useLookup("/api/insurance-companies", {
    pick: (data) => data?.companies,
  });
  const policyTypes = useLookup("/api/insurance-policy-types", {
    pick: (data) => data?.policy_types,
  });

  const dateError =
    d.start_date && d.end_date && d.end_date < d.start_date
      ? "End date must be after start date"
      : "";

  const selectedCompany = companies.data.find(
    (c) => String(c.id) === String(d.insurance_company_id),
  );
  const coverageOptions = (selectedCompany?.coverage_types || [])
    .filter((c) => c.is_active !== false)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((c) => ({ value: c.id, label: `${c.name_en} — ${c.name_ar}` }));

  return (
    <Modal
      title="Insurance"
      subtitle="Insurance policy details"
      icon={ShieldCheck}
      onClose={onClose}
      width={700}
      footer={
        <Footer
          onCancel={onClose}
          disabled={!!dateError}
          onRemove={
            value
              ? () => {
                  onSave(null);
                  onClose();
                }
              : null
          }
          onSave={() => {
            onSave(d);
            onClose();
          }}
        />
      }
    >
      <div className="vc-grid vc-grid--2">
        <Field label="Insurance Company">
          <Select
            value={d.insurance_company_id}
            loading={companies.loading}
            placeholder="Select company"
            options={companies.data.map((c) => ({
              value: c.id,
              label: `${c.name_en} — ${c.name_ar}`,
            }))}
            onChange={(v) =>
              setD((p) => ({
                ...p,
                insurance_company_id: v,
                coverage_type_ids: [],
              }))
            }
          />
        </Field>
        <Field label="Policy Type">
          <Select
            value={d.policy_type_id}
            loading={policyTypes.loading}
            placeholder="Select policy type"
            options={policyTypes.data.map((p) => ({
              value: p.id,
              label: `${p.name_en} — ${p.name_ar}`,
            }))}
            onChange={(v) => set("policy_type_id", v)}
          />
        </Field>
                <Field label="Coverages" className="vc-col-full">
          <MultiSelect
            values={d.coverage_type_ids}
            options={coverageOptions}
            disabled={!d.insurance_company_id}
            placeholder={
              d.insurance_company_id
                ? "Select coverages"
                : "Select an insurance company first"
            }
            onChange={(ids) => set("coverage_type_ids", ids)}
          />
        </Field>
        <Field label="Policy Number" className="vc-col-full">
          <input
            type="number"
            className="vc-input"
            placeholder="Enter policy number"
            value={d.policy_number}
            onChange={(e) => set("policy_number", e.target.value)}
          />
        </Field>
        <Field label="Start Date">
          <input
            type="date"
            className="vc-input"
            value={d.start_date}
            onChange={(e) => set("start_date", e.target.value)}
          />
        </Field>
        <Field label="End Date" error={dateError}>
          <input
            type="date"
            className="vc-input"
            min={d.start_date || undefined}
            value={d.end_date}
            onChange={(e) => set("end_date", e.target.value)}
          />
        </Field>

        <Field label="Insurance Document (one file)" className="vc-col-full">
          <FileUploader
            file={d.document}
            onChange={(f) => set("document", f)}
          />
        </Field>
        <Field label="Insurance Images" className="vc-col-full">
          <ImageUploader
            files={d.images}
            onChange={(f) => set("images", f)}
            max={8}
            compact
          />
        </Field>
      </div>
    </Modal>
  );
}
